import { checkIsInternalCompany } from 'app/auth/authUtils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { FeatureFlag } from 'constants/featureFlags';
import { cureTypeOptions, FeeCalculationType, FeeCureTxCoType } from 'constants/feeTypes';
import { leSectionOptions, leSectionRestrictedOptions } from 'constants/options';
import { resolveFeeProviderCategoryIds } from 'utils/feeTypes';
import searchItems from 'utils/searchItems';

import { getIsFromInternalCompany } from 'store/auth/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { selectFeeProviderCategoriesValues } from './feeProviderCategory.selectors';
import { getSystem } from './system.selectors';

const selectFeeTypesState = createSelector(getSystem, ({ feeTypes }) => feeTypes);

export const selectFeeTypes = createSelector(selectFeeTypesState, feeTypes =>
  _.mapValues(feeTypes, feeType => ({
    ...feeType,
    // It's business condition. BE cannot send data otherwise
    isEnabled: feeType.calculationType === FeeCalculationType.AUTOMATED ? true : feeType.isEnabled,
  })),
);

export const selectFeeTypeById = memoizeOne(id =>
  createSelector(selectFeeTypes, feeTypes => feeTypes[id]),
);

export const selectFeeTypesWithExcludedIds = memoizeOne(ids =>
  createSelector(selectFeeTypes, feeTypes =>
    _.omitBy(_.omit(feeTypes, ids), ({ isDeleted }) => isDeleted),
  ),
);

const selectFeeTypesValues = createSelector(selectFeeTypes, Object.values);

const selectFeeTypesOptions = createSelector(selectFeeTypesValues, feeTypes =>
  feeTypes.map(
    ({
      name,
      feeTypeId,
      isSellerPaid,
      cureType,
      isDeleted,
      feeProviderCategoryId,
      feeProviderCategoryIds,
      isEnabled,
      calculationType,
      leSection,
      recordingType,
      countTowardsApr,
    }) => ({
      name,
      value: feeTypeId,
      isSellerPaid,
      cureType,
      isDeleted,
      feeProviderCategoryId,
      feeProviderCategoryIds: resolveFeeProviderCategoryIds(
        feeProviderCategoryIds,
        feeProviderCategoryId,
      ),
      isEnabled,
      leSection,
      isCustomDisabled: !isEnabled && calculationType === FeeCalculationType.CUSTOM,
      recordingType,
      countTowardsApr,
    }),
  ),
);

const selectSortedFeeTypesOptions = createSelector(selectFeeTypesOptions, feeTypesOptions =>
  _.sortBy(feeTypesOptions, [option => _.toLower(option.name)]),
);

export const selectFeeTypesOptionsWithoutSellerPaid = createSelector(
  [selectSortedFeeTypesOptions],
  feeTypes => feeTypes.filter(({ isSellerPaid }) => !isSellerPaid),
);

export const selectUniqueFeeTypesOptions = memoizeOne(
  (feeTypesUsedIds, currentFeeTypeId) =>
    createSelector(selectFeeTypesOptionsWithoutSellerPaid, options =>
      options.filter(({ value }) => value === currentFeeTypeId || !feeTypesUsedIds.includes(value)),
    ),
  _.isEqual,
);

export const selectFeeTypesSchema = createSelector(
  getSystem,
  ({ feeTypesSchema }) => feeTypesSchema,
);

const selectFormattedFeeTypesValues = createSelector(
  [
    selectFeeTypesValues,
    selectFeeProviderCategoriesValues,
    getIsFromInternalCompany,
    selectFeatureFlagByKey(FeatureFlag.EnableLeSectionSelectionRestrictions),
  ],
  (
    feeTypes,
    feeProviderCategoryOptions,
    isInternalCompany,
    isEnableLeSectionSelectionRestrictions,
  ) => {
    const leOptions = isEnableLeSectionSelectionRestrictions
      ? leSectionRestrictedOptions
      : leSectionOptions;

    return feeTypes.map(feeType => {
      const feeProviderCategoryIds = resolveFeeProviderCategoryIds(
        feeType.feeProviderCategoryIds,
        feeType.feeProviderCategoryId,
      );

      const feeProviderCategoryNames = feeProviderCategoryIds
        .map(
          id =>
            feeProviderCategoryOptions.find(
              ({ feeProviderCategoryId }) => feeProviderCategoryId === id,
            )?.name,
        )
        .filter(Boolean)
        .join(', ');

      return {
        ...feeType,
        isGlobal: !isInternalCompany && checkIsInternalCompany(feeType),
        leSection: leOptions.find(({ value }) => value === feeType.leSection)?.name,
        feeProviderCategoryId: feeProviderCategoryNames,
        cureType: cureTypeOptions.find(({ value }) => value === feeType.cureType)?.name,
        isSellerPaid: feeType.isSellerPaid ? 'Seller' : 'Borrower',
        excludeFromTexasCashOutCure: feeType.excludeFromTexasCashOutCure
          ? FeeCureTxCoType.EXCLUDED
          : FeeCureTxCoType.INCLUDED,
      };
    });
  },
);

export const selectFilteredFeeTypes = createSelector(
  getDebouncedSearchValue,
  selectFormattedFeeTypesValues,
  searchItems(feeType => [feeType.name]),
);
